/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from "react";
import Card from "../components/ui/Card";
import StatCard from "../components/ui/StatCard";
import Icon from "../components/ui/Icon";
import { BarChart } from "../components/charts/BarChart";
import { generateChartData } from "../utils/dashbaord-utils/generateChartData";
import { LineChart } from "../components/charts/LineChart";
import { PieChart } from "../components/charts/PieChart";
import AppLoader from "../components/ui/AppLoader";

import {
  DatePicker,
  Space,
  Typography,
  message,
  Input,
  Button,
  Radio,
  Popover,
  Select,
  Tabs,
  Collapse,
  Tag,
  Descriptions,
  Pagination,
} from "antd";
import { FilterOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { getFilteredCalls, getAvailableFilters } from "../api/twillo-api";
import { formatToStartCase } from "../utils/basic-utils/formatToStartCase";
import { generateStatsFromCalls } from "../utils/dashbaord-utils/generateStatsFromCalls";
import { getOrgId } from "../utils/authHelpers";
import { checkCustomerByPhone } from "../api/customer-lookup-api";
import { formatCurrency, formatNumber } from "../utils/format";

const { RangePicker } = DatePicker;
const { Text } = Typography;

export function DashboardPage({ setCurrentPage }) {
  const organizationId = getOrgId();
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(6, "day"),
    dayjs(),
  ]);
  const [dashboardData, setDashboardData] = useState({
    stats: [],
    calls: [],
  });
  const [loadingData, setLoadingData] = useState(false);

  const [availableFilters, setAvailableFilters] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [selectedFilterKey, setSelectedFilterKey] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [trafficScopeDate, setTrafficScopeDate] = useState("all");
  const [durationUnit, setDurationUnit] = useState("minutes");
  const [activeTab, setActiveTab] = useState("overview");
  const [callLogSearch, setCallLogSearch] = useState("");
  const [callLogStatus, setCallLogStatus] = useState("all");
  const [callLogPage, setCallLogPage] = useState(1);
  const [callLogPageSize, setCallLogPageSize] = useState(10);
  const [callerBreakdownPage, setCallerBreakdownPage] = useState(1);
  const [callerBreakdownPageSize, setCallerBreakdownPageSize] = useState(10);
  const [customerLookupMap, setCustomerLookupMap] = useState({});
  const chartData = useMemo(() => {
    return generateChartData(dashboardData.calls);
  }, [dashboardData.calls]);

  const availableCallDates = useMemo(() => {
    return Array.from(
      new Set(
        dashboardData.calls
          .map((call) => dayjs(call.start_time))
          .filter((time) => time.isValid())
          .map((time) => time.format("YYYY-MM-DD")),
      ),
    ).sort((a, b) => new Date(a) - new Date(b));
  }, [dashboardData.calls]);

  const trafficScopedCalls = useMemo(() => {
    if (trafficScopeDate === "all") return dashboardData.calls;
    return dashboardData.calls.filter(
      (call) =>
        dayjs(call.start_time).isValid() &&
        dayjs(call.start_time).format("YYYY-MM-DD") === trafficScopeDate,
    );
  }, [dashboardData.calls, trafficScopeDate]);

  const trafficChartData = useMemo(() => {
    return generateChartData(trafficScopedCalls);
  }, [trafficScopedCalls]);

  const durationUnitMeta = useMemo(() => {
    if (durationUnit === "seconds") return { divisor: 1, suffix: "sec" };
    if (durationUnit === "hours") return { divisor: 3600, suffix: "hr" };
    return { divisor: 60, suffix: "min" };
  }, [durationUnit]);

  const averageDurationTrendByUnit = useMemo(() => {
    return chartData.avgDurationTrend.values.map((valueInSeconds) =>
      +(valueInSeconds / durationUnitMeta.divisor).toFixed(2),
    );
  }, [chartData.avgDurationTrend.values, durationUnitMeta.divisor]);

  const trafficScopeOptions = useMemo(
    () => [
      { label: "Overall (Selected Range)", value: "all" },
      ...availableCallDates.map((date) => ({ label: date, value: date })),
    ],
    [availableCallDates],
  );

  const trafficScopeLabel = useMemo(() => {
    return trafficScopeDate === "all"
      ? "Overall in selected date range"
      : `Date: ${trafficScopeDate}`;
  }, [trafficScopeDate]);

  const callStatuses = useMemo(() => {
    const statuses = Array.from(
      new Set(dashboardData.calls.map((call) => call.status).filter(Boolean)),
    ).sort();
    return [{ label: "All Statuses", value: "all" }].concat(
      statuses.map((status) => ({
        label: formatToStartCase(status),
        value: status,
      })),
    );
  }, [dashboardData.calls]);

  function isInProgressStatus(status) {
    const normalized = String(status || "")
      .trim()
      .toLowerCase()
      .replace(/[_\s]+/g, "-");
    return normalized === "in-progress";
  }

  function normalizePhone(value) {
    const phone = String(value || "").trim();
    if (!phone) return "";
    if (/^client:/i.test(phone) || /^anonymous$/i.test(phone)) return "";
    if (!/[0-9]/.test(phone)) return "";
    return phone;
  }

  const callerNumbers = useMemo(() => {
    const numbers = new Set();
    dashboardData.calls.forEach((call) => {
      const phone = normalizePhone(call.from_formatted || call.from);
      if (phone) numbers.add(phone);
    });
    return Array.from(numbers);
  }, [dashboardData.calls]);

  const uniqueNumbersAnalytics = useMemo(() => {
    const normalize = (value) => String(value || "").trim();

    const fromMap = {};
    const toMap = {};
    const combinedSet = new Set();

    dashboardData.calls.forEach((call) => {
      const fromNumber = normalize(call.from_formatted || call.from);
      const toNumber = normalize(call.to_formatted || call.to);

      if (fromNumber) {
        fromMap[fromNumber] = (fromMap[fromNumber] || 0) + 1;
        combinedSet.add(fromNumber);
      }

      if (toNumber) {
        toMap[toNumber] = (toMap[toNumber] || 0) + 1;
        combinedSet.add(toNumber);
      }
    });

    const sortedFrom = Object.entries(fromMap)
      .map(([number, count]) => ({ number, count }))
      .sort((a, b) => b.count - a.count);
    const topFrom = sortedFrom.slice(0, 8);

    const sortedTo = Object.entries(toMap)
      .map(([number, count]) => ({ number, count }))
      .sort((a, b) => b.count - a.count);
    const topTo = sortedTo.slice(0, 8);

    const repeatCallers = Object.values(fromMap).filter((count) => count > 1).length;
    const repeatDestinations = Object.values(toMap).filter((count) => count > 1).length;

    return {
      uniqueFromCount: Object.keys(fromMap).length,
      uniqueToCount: Object.keys(toMap).length,
      uniqueCombinedCount: combinedSet.size,
      repeatCallers,
      repeatDestinations,
      sortedFrom,
      sortedTo,
      topFrom,
      topTo,
    };
  }, [dashboardData.calls]);

  const filteredCallLogs = useMemo(() => {
    const searchTerm = callLogSearch.trim().toLowerCase();
    return [...dashboardData.calls]
      .filter((call) => {
        if (callLogStatus !== "all" && call.status !== callLogStatus) {
          return false;
        }
        if (!searchTerm) return true;
        return (
          String(call.sid || "").toLowerCase().includes(searchTerm) ||
          String(call.from || "").toLowerCase().includes(searchTerm) ||
          String(call.to || "").toLowerCase().includes(searchTerm) ||
          String(call.status || "").toLowerCase().includes(searchTerm)
        );
      })
      .sort(
        (a, b) =>
          dayjs(b.start_time).valueOf() - dayjs(a.start_time).valueOf(),
      );
  }, [dashboardData.calls, callLogSearch, callLogStatus]);

  const inProgressCalls = useMemo(() => {
    return [...dashboardData.calls]
      .filter((call) => isInProgressStatus(call.status))
      .sort((a, b) => dayjs(b.start_time).valueOf() - dayjs(a.start_time).valueOf());
  }, [dashboardData.calls]);

  const registrationStats = useMemo(() => {
    const callerSet = new Set(callerNumbers);
    const registeredSet = new Set();
    const unregisteredSet = new Set();

    callerSet.forEach((phone) => {
      const profile = customerLookupMap[phone];
      if (!profile) return;
      if (profile.is_registered) registeredSet.add(phone);
      else unregisteredSet.add(phone);
    });

    const totalKnown = registeredSet.size + unregisteredSet.size;
    const rate = totalKnown ? +((registeredSet.size / totalKnown) * 100).toFixed(1) : 0;

    return {
      registeredUnique: registeredSet.size,
      unregisteredUnique: unregisteredSet.size,
      registrationRate: rate,
    };
  }, [callerNumbers, customerLookupMap]);

  const displayStats = useMemo(() => {
    return [
      ...(dashboardData.stats || []),
      {
        key: "registered-callers",
        label: "Registered Callers",
        value: registrationStats.registeredUnique,
        icon: "users",
        trend: "up",
        delta: 0,
      },
      {
        key: "unregistered-callers",
        label: "Unregistered Callers",
        value: registrationStats.unregisteredUnique,
        icon: "user",
        trend: "up",
        delta: 0,
      },
      {
        key: "caller-reg-rate",
        label: "Caller Reg Rate (%)",
        value: registrationStats.registrationRate,
        icon: "shield",
        trend: "up",
        delta: 0,
      },
    ];
  }, [dashboardData.stats, registrationStats]);

  const primaryStatKeys = useMemo(
    () => ["calls", "completion-rate", "avg-duration", "avg-queue"],
    [],
  );

  const primaryStats = useMemo(
    () => displayStats.filter((stat) => primaryStatKeys.includes(stat.key)),
    [displayStats, primaryStatKeys],
  );

  const secondaryStats = useMemo(
    () => displayStats.filter((stat) => !primaryStatKeys.includes(stat.key)),
    [displayStats, primaryStatKeys],
  );

  function formatStatDisplayValue(stat) {
    if (!stat) return "-";
    const numericValue = Number(stat.value);
    if (stat.isCurrency) return formatCurrency(Number.isFinite(numericValue) ? numericValue : 0);
    if (Number.isFinite(numericValue)) return formatNumber(numericValue);
    return String(stat.value ?? "-");
  }

  function formatCostAmount(value) {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return "$0.00";
    return numericValue.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  const costAnalytics = useMemo(() => {
    const labels = chartData.costTrend?.labels || [];
    const values = chartData.costTrend?.values || [];
    const totalCost = values.reduce((sum, value) => sum + (Number(value) || 0), 0);
    const averageDailyCost = labels.length ? totalCost / labels.length : 0;
    const averageCostPerCall = dashboardData.calls.length
      ? totalCost / dashboardData.calls.length
      : 0;

    const highestCost = values.reduce(
      (best, value, index) => {
        const numericValue = Number(value) || 0;
        if (numericValue > best.value) {
          return { value: numericValue, date: labels[index] || "-" };
        }
        return best;
      },
      { value: 0, date: "-" },
    );

    return {
      totalCost,
      averageDailyCost,
      averageCostPerCall,
      highestCostDay: highestCost.date,
      highestCostValue: highestCost.value,
    };
  }, [chartData.costTrend, dashboardData.calls.length]);

  const paginatedCallLogs = useMemo(() => {
    const start = (callLogPage - 1) * callLogPageSize;
    return filteredCallLogs.slice(start, start + callLogPageSize);
  }, [filteredCallLogs, callLogPage, callLogPageSize]);

  const paginatedCallerBreakdown = useMemo(() => {
    const start = (callerBreakdownPage - 1) * callerBreakdownPageSize;
    return uniqueNumbersAnalytics.sortedFrom.slice(
      start,
      start + callerBreakdownPageSize,
    );
  }, [
    uniqueNumbersAnalytics.sortedFrom,
    callerBreakdownPage,
    callerBreakdownPageSize,
  ]);

  useEffect(() => {
    setCallLogPage(1);
  }, [callLogSearch, callLogStatus, dateRange]);

  useEffect(() => {
    setCallerBreakdownPage(1);
  }, [dateRange, dashboardData.calls]);

  useEffect(() => {
    if (
      trafficScopeDate !== "all" &&
      !availableCallDates.includes(trafficScopeDate)
    ) {
      setTrafficScopeDate("all");
    }
  }, [availableCallDates, trafficScopeDate]);
  useEffect(() => {
    setCurrentPage("/dashboard");
  }, []);

  const fetchFilters = async () => {
    try {
      setLoadingFilters(true);
      const response = await getAvailableFilters();
      const filterList = response?.filters || [];

      if (filterList.length > 0) {
        setAvailableFilters(filterList);
      } else {
        console.warn(" No filters returned from API");
        message.warning("No filters available");
      }
    } catch (err) {
      console.error("Filter fetch error:", err);
    } finally {
      setLoadingFilters(false);
    }
  };

  const fetchData = async (
    fromDate,
    toDate,
    selectedFilterKey = null,
    searchValue = "",
  ) => {
    try {
      setLoadingData(true);
      if (!organizationId) {
        message.error("Organization ID not found");
        setDashboardData({ stats: [], calls: [] });
        return;
      }

      const filtersArray = [{ key: "to", value: organizationId }];
      if (selectedFilterKey && searchValue) {
        filtersArray.push({ key: selectedFilterKey, value: searchValue });
      }

      console.log("Payload:", {
        filters: filtersArray,
        limit: 1000,
      });

      const response = await getFilteredCalls(1000, filtersArray);

      const fromBoundary = dayjs(fromDate).startOf("day");
      const toBoundary = dayjs(toDate).endOf("day");
      const allCalls = response?.calls || response?.data || [];
      const calls = allCalls.filter((call) => {
        const startTime = dayjs(call.start_time);
        if (!startTime.isValid()) return false;
        return (
          startTime.isSame(fromBoundary, "day") ||
          startTime.isSame(toBoundary, "day") ||
          (startTime.isAfter(fromBoundary) && startTime.isBefore(toBoundary))
        );
      });

      const generatedStats = generateStatsFromCalls(calls);

      setDashboardData({
        stats: generatedStats,
        calls,
      });
    } catch (err) {
      console.error("Data fetch error:", err);
      message.error("Failed to load dashboard data");
      setDashboardData({ stats: [], calls: [] });
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  useEffect(() => {
    if (!dateRange?.[0] || !dateRange?.[1]) return;
    const from = dateRange[0].format("YYYY-MM-DD");
    const to = dateRange[1].format("YYYY-MM-DD");

    fetchData(from, to, selectedFilterKey, searchValue);
  }, [dateRange]);

  const handleRangeChange = (dates) => {
    setDateRange(dates);
  };

  useEffect(() => {
    const numbersToLookup = callerNumbers.filter((phone) => !customerLookupMap[phone]);
    if (!numbersToLookup.length) return;

    let cancelled = false;

    const runLookup = async () => {
      const results = await Promise.allSettled(
        numbersToLookup.map(async (phone) => {
          try {
            const data = await checkCustomerByPhone(phone);
            return { phone, data };
          } catch (error) {
            return { phone, data: { is_registered: false } };
          }
        }),
      );

      if (cancelled) return;

      setCustomerLookupMap((prev) => {
        const next = { ...prev };
        results.forEach((result) => {
          if (result.status === "fulfilled") {
            next[result.value.phone] = result.value.data;
          }
        });
        return next;
      });
    };

    runLookup();

    return () => {
      cancelled = true;
    };
  }, [callerNumbers, customerLookupMap]);

  const handleRefresh = () => {
    if (!dateRange?.[0] || !dateRange?.[1]) return;
    const from = dateRange[0].format("YYYY-MM-DD");
    const to = dateRange[1].format("YYYY-MM-DD");
    fetchData(from, to, selectedFilterKey, searchValue);
  };

  return (
    <div className="page">
      <div className="page-header dashboard-page-header">
        <div>
          <h1 className="page-title">Dashboard Overview</h1>
          <p className="page-subtitle">
            Monitor your key metrics and performance
          </p>
        </div>

        <div className="dashboard-header-controls">
          <Space className="dashboard-date-control" orientation="vertical" size={4}>
            <Text type="secondary" className="dashboard-control-label">
              Date Range
            </Text>
            <RangePicker
              className="dashboard-range-picker"
              value={dateRange}
              allowClear={false}
              onChange={handleRangeChange}
              disabledDate={(current) =>
                current && current > dayjs().endOf("day")
              }
            />
          </Space>

          <Input
            className="dashboard-search-input"
            placeholder={
              selectedFilterKey
                ? `Search by ${formatToStartCase(selectedFilterKey)}`
                : "Search..."
            }
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />

          <div className="dashboard-header-btn-group">
            <Popover
              trigger="click"
              open={filterDropdownOpen}
              onOpenChange={(open) => {
                if (open) {
                  setFilterDropdownOpen(true);
                }
              }}
              placement="bottomRight"
              content={
                <div className="dashboard-filter-popover">
                  <Text strong>Filter By</Text>

                  <Radio.Group
                    className="dashboard-filter-radio-group"
                    value={selectedFilterKey}
                    onChange={(e) => setSelectedFilterKey(e.target.value)}
                  >
                    {availableFilters
                      .filter((filter) => filter !== "to")
                      .map((filter) => (
                      <Radio key={filter} value={filter}>
                        {formatToStartCase(filter)}
                      </Radio>
                      ))}
                  </Radio.Group>

                  <div className="dashboard-filter-actions">
                    <Button
                      block
                      onClick={() => {
                        setSelectedFilterKey(null);
                        setSearchValue("");
                        const from = dateRange[0].format("YYYY-MM-DD");
                        const to = dateRange[1].format("YYYY-MM-DD");
                        fetchData(from, to);
                        setFilterDropdownOpen(false);
                      }}
                    >
                      Clear
                    </Button>

                    <Button
                      type="primary"
                      block
                      disabled={!selectedFilterKey || !searchValue}
                      onClick={() => {
                        const from = dateRange[0].format("YYYY-MM-DD");
                        const to = dateRange[1].format("YYYY-MM-DD");
                        fetchData(from, to, selectedFilterKey, searchValue);
                        setFilterDropdownOpen(false);
                      }}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              }
            >
              <Button
                className="dashboard-compact-btn dashboard-filter-trigger"
                icon={<FilterOutlined />}
                onClick={() => setFilterDropdownOpen(true)}
              >
                Filter
              </Button>
            </Popover>
            <Button
              className="dashboard-compact-btn dashboard-refresh-btn"
              icon={<ReloadOutlined />}
              loading={loadingData}
              onClick={handleRefresh}
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="stack-lg">
        {loadingData ? (
          <AppLoader
            variant="page"
            label="Loading dashboard data"
            sublabel="Syncing calls and analytics..."
          />
        ) : (
          <>
            <div className="stack-lg">
              <div className="dashboard-stats-wrap">
                <div className="dashboard-stats-primary">
                  {primaryStats.map((stat) => (
                    <StatCard key={stat.key} stat={stat} />
                  ))}
                </div>
                {secondaryStats.length ? (
                  <div className="dashboard-stats-secondary">
                    {secondaryStats.map((stat) => (
                      <div key={stat.key} className="kpi-chip">
                        <div className="kpi-chip-icon" aria-hidden="true">
                          <Icon name={stat.icon || "dashboard"} size={16} />
                        </div>
                        <div className="kpi-chip-meta">
                          <div className="kpi-chip-label">{stat.label}</div>
                          <div className="kpi-chip-value">{formatStatDisplayValue(stat)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>

              {inProgressCalls.length ? (
                <Card
                  title={`In-Progress Calls (${inProgressCalls.length})`}
                  subtitle="Active calls currently in progress."
                >
                  <div className="stack-md">
                    {inProgressCalls.slice(0, 3).map((call) => {
                      const startTime = dayjs(call.start_time);
                      const callerPhone = normalizePhone(call.from_formatted || call.from);
                      const customerProfile = callerPhone
                        ? customerLookupMap[callerPhone]
                        : null;
                      const callerDisplay =
                        customerProfile?.is_registered && customerProfile?.name
                          ? customerProfile.name
                          : call.from_formatted || call.from || "Unknown";

                      return (
                        <div
                          key={call.sid || `${call.start_time}-${call.to}`}
                          className="live-call-panel"
                        >
                          <div className="call-log-main">
                            <Text strong>{call.sid || "Unknown SID"}</Text>
                            <Tag color="blue">In-Progress</Tag>
                            <Tag>{formatToStartCase(call.direction || "unknown")}</Tag>
                          </div>
                          <div className="call-log-meta">
                            <Text type="secondary">
                              {startTime.isValid()
                                ? startTime.format("YYYY-MM-DD HH:mm")
                                : "No start time"}
                            </Text>
                            <Text type="secondary">Duration: {call.duration || 0}s</Text>
                            <Text type="secondary">
                              {callerDisplay} -> {call.to_formatted || call.to || "Unknown"}
                            </Text>
                          </div>
                          <Descriptions
                            size="small"
                            column={{ xs: 1, sm: 2, md: 3 }}
                            bordered
                            items={[
                              {
                                key: "status",
                                label: "Status",
                                children: formatToStartCase(call.status || "in-progress"),
                              },
                              {
                                key: "direction",
                                label: "Direction",
                                children: formatToStartCase(call.direction || "unknown"),
                              },
                              {
                                key: "duration",
                                label: "Duration",
                                children: `${call.duration || 0} seconds`,
                              },
                              {
                                key: "from",
                                label: "From",
                                children: call.from_formatted || call.from || "-",
                              },
                              {
                                key: "to",
                                label: "To",
                                children: call.to_formatted || call.to || "-",
                              },
                              {
                                key: "customer_registration",
                                label: "Customer Registered",
                                children: customerProfile
                                  ? customerProfile.is_registered
                                    ? "Yes"
                                    : "No"
                                  : "Checking...",
                              },
                              {
                                key: "customer_name",
                                label: "Customer Name",
                                children:
                                  customerProfile?.is_registered && customerProfile?.name
                                    ? customerProfile.name
                                    : "-",
                              },
                              {
                                key: "customer_email",
                                label: "Customer Email",
                                children:
                                  customerProfile?.is_registered && customerProfile?.email
                                    ? customerProfile.email
                                    : "-",
                              },
                              {
                                key: "start_time",
                                label: "Start Time",
                                children: startTime.isValid()
                                  ? startTime.format("YYYY-MM-DD HH:mm:ss")
                                  : "-",
                              },
                            ]}
                          />
                        </div>
                      );
                    })}

                    {inProgressCalls.length > 3 ? (
                      <Text type="secondary">
                        Showing 3 of {inProgressCalls.length} in-progress calls.
                      </Text>
                    ) : null}
                  </div>
                </Card>
              ) : null}

              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                  {
                    key: "overview",
                    label: "Overview",
                    children: (
                      <div className="stack-lg">
                        <Collapse
                          defaultActiveKey={["overview-volume", "overview-usage"]}
                          items={[
                            {
                              key: "overview-volume",
                              label: "Volume and Outcomes",
                              children: (
                                <div className="grid-2">
                                  <Card
                                    title="Calls Over Time"
                                    subtitle="Daily call volume trend across selected range."
                                  >
                                    <LineChart
                                      labels={chartData.callsOverTime.labels}
                                      values={chartData.callsOverTime.values}
                                      xTitle="Date"
                                      yTitle="Number of Calls"
                                      yAxisMinInterval={1}
                                    />
                                  </Card>

                                  <Card
                                    title="Call Outcome Distribution"
                                    subtitle="How calls ended by status."
                                  >
                                    <PieChart data={chartData.statusDistribution} />
                                  </Card>
                                </div>
                              ),
                            },
                            {
                              key: "overview-usage",
                              label: "Usage and Demand Peaks",
                              children: (
                                <div className="grid-2">
                                  <Card
                                    title="Minutes Usage Trend"
                                    subtitle="Total minutes consumed each day."
                                  >
                                    <LineChart
                                      labels={chartData.minutesTrend.labels}
                                      values={chartData.minutesTrend.values}
                                      xTitle="Date"
                                      yTitle="Minutes Used"
                                      xAxisLabelRotate={30}
                                    />
                                  </Card>

                                  <Card
                                    title="Busiest Dates (Top 7)"
                                    subtitle="Highest-volume dates in selected range."
                                  >
                                    <BarChart
                                      labels={chartData.busiestDates.labels}
                                      values={chartData.busiestDates.values}
                                      xTitle="Date"
                                      yTitle="Call Volume"
                                      xAxisLabelRotate={30}
                                      yAxisMinInterval={1}
                                    />
                                  </Card>
                                </div>
                              ),
                            },
                          ]}
                        />
                      </div>
                    ),
                  },
                  {
                    key: "traffic-patterns",
                    label: "Traffic Patterns",
                    children: (
                      <div className="stack-lg">
                        <div className="dashboard-toolbar">
                          <Text type="secondary">Traffic Scope</Text>
                          <Select
                            className="dashboard-traffic-scope-select"
                            size="middle"
                            value={trafficScopeDate}
                            options={trafficScopeOptions}
                            onChange={setTrafficScopeDate}
                          />
                        </div>

                        <Collapse
                          defaultActiveKey={["traffic-intraday", "traffic-weekly"]}
                          items={[
                            {
                              key: "traffic-intraday",
                              label: "Intra-Day Traffic Distribution",
                              children: (
                                <div className="grid-2">
                                  <Card
                                    title="Calls by Hour of Day"
                                    subtitle={trafficScopeLabel}
                                  >
                                    <BarChart
                                      labels={trafficChartData.callsByHourOfDay.labels}
                                      values={trafficChartData.callsByHourOfDay.values}
                                      xTitle="Hour of Day"
                                      yTitle="Calls"
                                      xAxisLabelRotate={45}
                                      xAxisLabelInterval={1}
                                      yAxisMinInterval={1}
                                    />
                                  </Card>

                                  <Card
                                    title="Peak 3-Hour Windows"
                                    subtitle={trafficScopeLabel}
                                  >
                                    <BarChart
                                      labels={trafficChartData.peakHourWindows.labels}
                                      values={trafficChartData.peakHourWindows.values}
                                      xTitle="Time Window"
                                      yTitle="Calls"
                                      yAxisMinInterval={1}
                                    />
                                  </Card>
                                </div>
                              ),
                            },
                            {
                              key: "traffic-weekly",
                              label: "Weekly and Window Patterns",
                              children: (
                                <div className="grid-2">
                                  <Card
                                    title="Busy Days of Week"
                                    subtitle={trafficScopeLabel}
                                  >
                                    <BarChart
                                      labels={trafficChartData.busyWeekdays.labels}
                                      values={trafficChartData.busyWeekdays.values}
                                      xTitle="Weekday"
                                      yTitle="Calls"
                                      yAxisMinInterval={1}
                                    />
                                  </Card>

                                  <Card
                                    title="3-Hour Window Distribution"
                                    subtitle={trafficScopeLabel}
                                  >
                                    <BarChart
                                      labels={trafficChartData.callsByHour.labels}
                                      values={trafficChartData.callsByHour.values}
                                      xTitle="Window"
                                      yTitle="Calls"
                                      yAxisMinInterval={1}
                                    />
                                  </Card>
                                </div>
                              ),
                            },
                          ]}
                        />
                      </div>
                    ),
                  },
                  {
                    key: "efficiency",
                    label: "Efficiency",
                    children: (
                      <div className="stack-lg">
                        <Collapse
                          defaultActiveKey={["efficiency-duration", "efficiency-queue"]}
                          items={[
                            {
                              key: "efficiency-duration",
                              label: "Call Handling Duration",
                              children: (
                                <div className="grid-2">
                                  <Card
                                    title="Average Duration Trend"
                                    subtitle="Track how long calls take over time."
                                    actions={
                                      <Select
                                        className="dashboard-duration-unit-select"
                                        size="small"
                                        value={durationUnit}
                                        options={[
                                          { label: "Minutes", value: "minutes" },
                                          { label: "Seconds", value: "seconds" },
                                          { label: "Hours", value: "hours" },
                                        ]}
                                        onChange={setDurationUnit}
                                      />
                                    }
                                  >
                                    <LineChart
                                      labels={chartData.avgDurationTrend.labels}
                                      values={averageDurationTrendByUnit}
                                      xTitle="Date"
                                      yTitle={`Avg Duration (${durationUnitMeta.suffix})`}
                                      xAxisLabelRotate={30}
                                    />
                                  </Card>
                                </div>
                              ),
                            },
                            {
                              key: "efficiency-queue",
                              label: "Queue and Wait Experience",
                              children: (
                                <div className="grid-2">
                                  <Card
                                    title="Queue Time Trend"
                                    subtitle="Average queue delay before calls are handled."
                                  >
                                    <LineChart
                                      labels={chartData.queueTimeTrend.labels}
                                      values={chartData.queueTimeTrend.values}
                                      xTitle="Date"
                                      yTitle="Avg Queue (sec)"
                                      xAxisLabelRotate={30}
                                    />
                                  </Card>
                                </div>
                              ),
                            },
                          ]}
                        />
                      </div>
                    ),
                  },
                  {
                    key: "cost-analytics",
                    label: "Cost Analytics",
                    children: (
                      <div className="stack-lg">
                        <div className="insight-grid">
                          <div className="insight-tile">
                            <div className="insight-label">Total Cost</div>
                            <div className="insight-value">
                              {formatCostAmount(costAnalytics.totalCost)}
                            </div>
                          </div>
                          <div className="insight-tile">
                            <div className="insight-label">Avg Daily Cost</div>
                            <div className="insight-value">
                              {formatCostAmount(costAnalytics.averageDailyCost)}
                            </div>
                          </div>
                          <div className="insight-tile">
                            <div className="insight-label">Avg Cost per Call</div>
                            <div className="insight-value">
                              {formatCostAmount(costAnalytics.averageCostPerCall)}
                            </div>
                          </div>
                          <div className="insight-tile">
                            <div className="insight-label">Highest Cost Day</div>
                            <div className="insight-value">
                              {costAnalytics.highestCostDay}
                            </div>
                            <div className="muted">
                              {formatCostAmount(costAnalytics.highestCostValue)}
                            </div>
                          </div>
                        </div>

                        <Collapse
                          defaultActiveKey={["cost-trends", "cost-breakdown"]}
                          items={[
                            {
                              key: "cost-trends",
                              label: "Cost Trends Over Time",
                              children: (
                                <div className="grid-2">
                                  <Card
                                    title="Cost Over Time"
                                    subtitle="Daily cost trend across selected range."
                                  >
                                    <LineChart
                                      labels={chartData.costTrend.labels}
                                      values={chartData.costTrend.values}
                                      xTitle="Date"
                                      yTitle="Cost (USD)"
                                      xAxisLabelRotate={30}
                                    />
                                  </Card>

                                  <Card
                                    title="Avg Cost per Call"
                                    subtitle="Daily average spend per call."
                                  >
                                    <LineChart
                                      labels={chartData.avgCostPerCallTrend.labels}
                                      values={chartData.avgCostPerCallTrend.values}
                                      xTitle="Date"
                                      yTitle="Avg Cost / Call (USD)"
                                      xAxisLabelRotate={30}
                                    />
                                  </Card>
                                </div>
                              ),
                            },
                            {
                              key: "cost-breakdown",
                              label: "Cost Breakdown",
                              children: (
                                <div className="grid-2">
                                  <Card
                                    title="Cost by Call Status"
                                    subtitle="Where spend is concentrated by outcome."
                                  >
                                    <PieChart data={chartData.costByStatus} />
                                  </Card>

                                  <Card
                                    title="Top Cost Dates"
                                    subtitle="Dates with the highest total spend."
                                  >
                                    <BarChart
                                      labels={[...chartData.costTrend.labels]
                                        .map((label, index) => ({
                                          label,
                                          value: chartData.costTrend.values[index] || 0,
                                        }))
                                        .sort((a, b) => b.value - a.value)
                                        .slice(0, 7)
                                        .map((item) => item.label)}
                                      values={[...chartData.costTrend.labels]
                                        .map((label, index) => ({
                                          label,
                                          value: chartData.costTrend.values[index] || 0,
                                        }))
                                        .sort((a, b) => b.value - a.value)
                                        .slice(0, 7)
                                        .map((item) => item.value)}
                                      xTitle="Date"
                                      yTitle="Cost (USD)"
                                      xAxisLabelRotate={30}
                                    />
                                  </Card>
                                </div>
                              ),
                            },
                          ]}
                        />
                      </div>
                    ),
                  },
                  {
                    key: "unique-numbers",
                    label: "Unique Numbers",
                    children: (
                      <div className="stack-lg">
                        <div className="insight-grid">
                          <div className="insight-tile">
                            <div className="insight-label">Unique Caller Numbers</div>
                            <div className="insight-value">
                              {uniqueNumbersAnalytics.uniqueFromCount}
                            </div>
                          </div>
                          <div className="insight-tile">
                            <div className="insight-label">Unique Dialed Numbers</div>
                            <div className="insight-value">
                              {uniqueNumbersAnalytics.uniqueToCount}
                            </div>
                          </div>
                          <div className="insight-tile">
                            <div className="insight-label">Total Unique Numbers</div>
                            <div className="insight-value">
                              {uniqueNumbersAnalytics.uniqueCombinedCount}
                            </div>
                          </div>
                        </div>

                        <div className="insight-grid">
                          <div className="insight-tile">
                            <div className="insight-label">Repeat Caller Numbers</div>
                            <div className="insight-value">
                              {uniqueNumbersAnalytics.repeatCallers}
                            </div>
                          </div>
                          <div className="insight-tile">
                            <div className="insight-label">Repeat Dialed Numbers</div>
                            <div className="insight-value">
                              {uniqueNumbersAnalytics.repeatDestinations}
                            </div>
                          </div>
                        </div>

                        <div className="grid-2">
                          <Card
                            title="Top Caller Numbers"
                            subtitle="Numbers that initiated the most calls."
                          >
                            <BarChart
                              labels={uniqueNumbersAnalytics.topFrom.map((item) =>
                                customerLookupMap[item.number]?.is_registered &&
                                customerLookupMap[item.number]?.name
                                  ? customerLookupMap[item.number].name
                                  : item.number,
                              )}
                              values={uniqueNumbersAnalytics.topFrom.map((item) => item.count)}
                              xTitle="Caller"
                              yTitle="Calls"
                              xAxisLabelRotate={35}
                              yAxisMinInterval={1}
                            />
                          </Card>

                          <Card
                            title="Top Dialed Numbers"
                            subtitle="Numbers most frequently called."
                          >
                            <BarChart
                              labels={uniqueNumbersAnalytics.topTo.map((item) => item.number)}
                              values={uniqueNumbersAnalytics.topTo.map((item) => item.count)}
                              xTitle="Dialed Number"
                              yTitle="Calls"
                              xAxisLabelRotate={35}
                              yAxisMinInterval={1}
                            />
                          </Card>
                        </div>

                        <div className="grid-2">
                          <Card title="Caller Breakdown" subtitle="Top caller numbers with call count.">
                            {uniqueNumbersAnalytics.sortedFrom.length ? (
                              <div className="table-wrap">
                                <table className="table">
                                  <thead>
                                    <tr>
                                      <th>Caller</th>
                                      <th>Number</th>
                                      <th>Calls</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {paginatedCallerBreakdown.map((item) => (
                                      <tr key={item.number}>
                                        <td className="table-strong">
                                          {customerLookupMap[item.number]?.is_registered &&
                                          customerLookupMap[item.number]?.name
                                            ? customerLookupMap[item.number].name
                                            : "Unregistered"}
                                        </td>
                                        <td className="table-muted-strong muted">
                                          {item.number}
                                        </td>
                                        <td className="table-muted-strong muted">{item.count}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="muted">No caller numbers available in selected range.</div>
                            )}
                            {uniqueNumbersAnalytics.sortedFrom.length ? (
                              <div className="dashboard-toolbar section-gap">
                                <Pagination
                                  current={callerBreakdownPage}
                                  pageSize={callerBreakdownPageSize}
                                  total={uniqueNumbersAnalytics.sortedFrom.length}
                                  showSizeChanger
                                  pageSizeOptions={["10", "20", "50"]}
                                  onChange={(page, pageSize) => {
                                    setCallerBreakdownPage(page);
                                    setCallerBreakdownPageSize(pageSize);
                                  }}
                                  showTotal={(total, range) =>
                                    `${range[0]}-${range[1]} of ${total}`
                                  }
                                />
                              </div>
                            ) : null}
                          </Card>

                          <Card title="Dialed Breakdown" subtitle="Top dialed numbers with call count.">
                            {uniqueNumbersAnalytics.topTo.length ? (
                              <div className="table-wrap">
                                <table className="table">
                                  <thead>
                                    <tr>
                                      <th>Dialed Number</th>
                                      <th>Calls</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {uniqueNumbersAnalytics.topTo.map((item) => (
                                      <tr key={item.number}>
                                        <td className="table-strong">{item.number}</td>
                                        <td className="table-muted-strong muted">{item.count}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="muted">No dialed numbers available in selected range.</div>
                            )}
                          </Card>
                        </div>
                      </div>
                    ),
                  },
                  {
                    key: "call-logs",
                    label: "Call Logs",
                    children: (
                      <div className="stack-lg">
                        <div className="dashboard-toolbar dashboard-toolbar-left">
                          <Input
                            className="dashboard-calllog-search"
                            placeholder="Search SID, from, to, status"
                            value={callLogSearch}
                            onChange={(event) =>
                              setCallLogSearch(event.target.value)
                            }
                          />
                          <Select
                            className="dashboard-calllog-status"
                            value={callLogStatus}
                            options={callStatuses}
                            onChange={setCallLogStatus}
                          />
                          <Text type="secondary">
                            Showing {filteredCallLogs.length} calls
                          </Text>
                        </div>

                        {filteredCallLogs.length ? (
                          <>
                            <Collapse
                              items={paginatedCallLogs.map((call) => {
                              const startTime = dayjs(call.start_time);
                              const endTime = dayjs(call.end_time);
                              const callerPhone = normalizePhone(
                                call.from_formatted || call.from,
                              );
                              const customerProfile = callerPhone
                                ? customerLookupMap[callerPhone]
                                : null;
                              const callerDisplay =
                                customerProfile?.is_registered &&
                                customerProfile?.name
                                  ? customerProfile.name
                                  : call.from_formatted || call.from || "Unknown";
                              return {
                                key: call.sid || `${call.start_time}-${call.to}`,
                                label: (
                                  <div className="call-log-header">
                                    <div className="call-log-main">
                                      <Text strong>{call.sid || "Unknown SID"}</Text>
                                      <Tag color="blue">
                                        {formatToStartCase(call.status || "unknown")}
                                      </Tag>
                                      <Tag>{formatToStartCase(call.direction || "unknown")}</Tag>
                                    </div>
                                    <div className="call-log-meta">
                                      <Text type="secondary">
                                        {startTime.isValid()
                                          ? startTime.format("YYYY-MM-DD HH:mm")
                                          : "No start time"}
                                      </Text>
                                      <Text type="secondary">
                                        Duration: {call.duration || 0}s
                                      </Text>
                                      <Text type="secondary">
                                        {callerDisplay} ->{" "}
                                        {call.to_formatted || call.to || "Unknown"}
                                      </Text>
                                    </div>
                                  </div>
                                ),
                                children: (
                                  (() => {
                                    return (
                                  <Descriptions
                                    size="small"
                                    column={{ xs: 1, sm: 2 }}
                                    bordered
                                    items={[
                                      {
                                        key: "sid",
                                        label: "Call SID",
                                        children: call.sid || "-",
                                      },
                                      {
                                        key: "status",
                                        label: "Status",
                                        children: formatToStartCase(
                                          call.status || "unknown",
                                        ),
                                      },
                                      {
                                        key: "direction",
                                        label: "Direction",
                                        children: formatToStartCase(
                                          call.direction || "unknown",
                                        ),
                                      },
                                      {
                                        key: "duration",
                                        label: "Duration",
                                        children: `${call.duration || 0} seconds`,
                                      },
                                      {
                                        key: "queue_time",
                                        label: "Queue Time",
                                        children: `${call.queue_time || 0} seconds`,
                                      },
                                      {
                                        key: "from",
                                        label: "From",
                                        children:
                                          call.from_formatted ||
                                          call.from ||
                                          "Anonymous/Unknown",
                                      },
                                      {
                                        key: "customer_registration",
                                        label: "Customer Registered",
                                        children: customerProfile
                                          ? customerProfile.is_registered
                                            ? "Yes"
                                            : "No"
                                          : "Checking...",
                                      },
                                      {
                                        key: "customer_name",
                                        label: "Customer Name",
                                        children:
                                          customerProfile?.is_registered &&
                                          customerProfile?.name
                                            ? customerProfile.name
                                            : "-",
                                      },
                                      {
                                        key: "customer_email",
                                        label: "Customer Email",
                                        children:
                                          customerProfile?.is_registered &&
                                          customerProfile?.email
                                            ? customerProfile.email
                                            : "-",
                                      },
                                      {
                                        key: "start_time",
                                        label: "Start Time",
                                        children: startTime.isValid()
                                          ? startTime.format(
                                              "YYYY-MM-DD HH:mm:ss",
                                            )
                                          : "-",
                                      },
                                      {
                                        key: "end_time",
                                        label: "End Time",
                                        children: endTime.isValid()
                                          ? endTime.format("YYYY-MM-DD HH:mm:ss")
                                          : "-",
                                      },
                                      {
                                        key: "date_created",
                                        label: "Created At",
                                        children: call.date_created
                                          ? dayjs(call.date_created).format(
                                              "YYYY-MM-DD HH:mm:ss",
                                            )
                                          : "-",
                                      },
                                    ]}
                                  />
                                    );
                                  })()
                                ),
                              };
                              })}
                            />
                            <div className="dashboard-toolbar">
                              <Pagination
                                current={callLogPage}
                                pageSize={callLogPageSize}
                                total={filteredCallLogs.length}
                                showSizeChanger
                                pageSizeOptions={["10", "20", "50"]}
                                onChange={(page, pageSize) => {
                                  setCallLogPage(page);
                                  setCallLogPageSize(pageSize);
                                }}
                                showTotal={(total, range) =>
                                  `${range[0]}-${range[1]} of ${total}`
                                }
                              />
                            </div>
                          </>
                        ) : (
                          <Card
                            title="No Call Logs Found"
                            subtitle="Try adjusting status or search filters."
                          />
                        )}
                      </div>
                    ),
                  },
                ]}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default DashboardPage;
